/**
 * Shared contact mutation hook for the `updateSharedContact` Cloud Function.
 *
 * Only supports update — shared contacts are created in AppSheet; deletion is
 * a soft-delete owned by the sync layer, not a client operation.
 *
 * @module hooks/fieldService/useSharedContactMutations
 *
 * @example
 * const { mutate: update } = useUpdateSharedContact();
 * update({ contactId: 'app1_CT1', updates: { primaryPhone: '555-0199' } });
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { dataHubContactKeys } from '../useDataHubContacts';
import toast from 'react-hot-toast';
import { PRE_EDIT_WRITE_CALLABLE_TIMEOUT_MS } from '../../config/appsheetWriteCallables';

/**
 * Query keys for the shared-contacts caches. `byIds` matches the key
 * GeneratorContacts has always used — the factory replaces its inline literal
 * without changing any key's value, so existing caches stay addressable.
 */
export const sharedContactKeys = {
  all: ['shared-contacts'] as const,
  byIds: (idsCsv: string, orgId: string | undefined) =>
    [...sharedContactKeys.all, 'byIds', idsCsv, orgId] as const,
  /**
   * The Tickets list's contacts (ISS-523). Its own key: `byIds` readers cap at
   * 30 contacts, this one does not, so the two must never share a cache entry.
   */
  forTicketList: (idsCsv: string, orgId: string | undefined) =>
    [...sharedContactKeys.all, 'forTicketList', idsCsv, orgId] as const,
};

/**
 * Display shape of one `appsheet_shared_contacts` row as the client caches it
 * (normalized from the bracketed AppSheet field names by useSharedContactsByIds
 * in GeneratorContacts).
 */
export interface SharedContact {
  /** Firestore doc id — what `updateSharedContact` addresses. */
  id: string;
  /** AppSheet 'Contact ID' (the table's own key). NOT the callable's contactId. */
  contactId: string;
  firstName: string;
  lastName: string;
  salutation: string;
  role: string;
  phone: string;
  email: string;
  company: string;
  branch: string;
  notes: string;
  commercialContact: boolean;
  /** Parsed from AppSheet's EnumList " , " serialization. */
  otherPhones: string[];
  otherEmails: string[];
}

/**
 * The callable's writable fields, keyed exactly as its FIELD_MAP expects
 * (functions/src/projects/sharedContactApi.ts — the edit-API/field-map
 * agreement guard #4166 keeps that map honest). List fields cross the wire as
 * string[]; the server owns EnumList serialization (normalizeListInput).
 */
export interface SharedContactUpdates {
  firstName?: string;
  lastName?: string;
  salutation?: string;
  company?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  role?: string;
  branch?: string;
  notes?: string;
  commercialContact?: boolean;
  otherPhones?: string[];
  otherEmails?: string[];
}

export interface UpdateSharedContactInput {
  /**
   * FIRESTORE DOC ID of the `appsheet_shared_contacts` row (SharedContact.id),
   * mirroring the callable's parameter name. This is NOT the AppSheet
   * 'Contact ID' that SharedContact.contactId carries — the two are different
   * strings for every row (doc ids are `<appId>_<sanitizedKey>`).
   */
  contactId: string;
  updates: SharedContactUpdates;
}

/**
 * Apply wire-shape updates to a cached SharedContact, returning a NEW object.
 * Maps the callable's FIELD_MAP keys onto the display shape
 * (primaryPhone -> phone, primaryEmail -> email; the rest are 1:1).
 */
function applySharedContactUpdates(
  contact: SharedContact,
  updates: SharedContactUpdates
): SharedContact {
  return {
    ...contact,
    ...(updates.firstName !== undefined && { firstName: updates.firstName }),
    ...(updates.lastName !== undefined && { lastName: updates.lastName }),
    ...(updates.salutation !== undefined && { salutation: updates.salutation }),
    ...(updates.company !== undefined && { company: updates.company }),
    ...(updates.primaryEmail !== undefined && { email: updates.primaryEmail }),
    ...(updates.primaryPhone !== undefined && { phone: updates.primaryPhone }),
    ...(updates.role !== undefined && { role: updates.role }),
    ...(updates.branch !== undefined && { branch: updates.branch }),
    ...(updates.notes !== undefined && { notes: updates.notes }),
    ...(updates.commercialContact !== undefined && {
      commercialContact: updates.commercialContact,
    }),
    ...(updates.otherPhones !== undefined && { otherPhones: updates.otherPhones }),
    ...(updates.otherEmails !== undefined && { otherEmails: updates.otherEmails }),
  };
}

/** A previously-cached list entry, kept for rollback: [queryKey, snapshot]. */
type ListSnapshot = [QueryKey, SharedContact[]];

/**
 * Update a shared contact's editable fields via Cloud Function, with an
 * optimistic patch of every shared-contacts cache holding the row.
 */
export function useUpdateSharedContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId, updates }: UpdateSharedContactInput) => {
      const fn = httpsCallable(functions, 'updateSharedContact', { timeout: PRE_EDIT_WRITE_CALLABLE_TIMEOUT_MS });
      await fn({ contactId, updates });
    },
    onMutate: async ({ contactId, updates }) => {
      await queryClient.cancelQueries({ queryKey: sharedContactKeys.all });

      // Patch every ['shared-contacts', ...] cache entry that holds the row.
      // Match on `id` (the Firestore doc id) — matching on the similarly-named
      // AppSheet contactId would patch the wrong row (or none).
      const entries = queryClient.getQueriesData<SharedContact[]>({
        queryKey: sharedContactKeys.all,
      });
      const previous: ListSnapshot[] = [];
      for (const [key, data] of entries) {
        if (!data?.some((c) => c.id === contactId)) continue;
        previous.push([key, data]);
        queryClient.setQueryData<SharedContact[]>(
          key,
          data.map((c) => (c.id === contactId ? applySharedContactUpdates(c, updates) : c))
        );
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      context?.previous?.forEach(([key, snapshot]) => {
        queryClient.setQueryData(key, snapshot);
      });
      // A multi-field save is atomic server-side, so one rejected field drops
      // the whole write. Callable HttpsError messages are validation text that
      // names the field and rule (the middleware genericizes anything
      // unexpected) — surfacing it is what makes that rejection legible.
      toast.error(
        err instanceof FirebaseError
          ? `Failed to update contact: ${err.message}`
          : 'Failed to update contact'
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: sharedContactKeys.all });
      // TechJobDetail's contact tab reads the same rows through the DataHub
      // contacts hooks — revalidate those too so an office edit shows up there.
      queryClient.invalidateQueries({ queryKey: dataHubContactKeys.all });
    },
    onSuccess: () => {
      toast.success('Contact updated');
    },
  });
}
